import { useEffect, useState } from 'react';
import API from '../../Services/api';
import RedeemButton from './RewardButton';

const typeImages = {
  discount: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&q=80',
  voucher:  'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=600&q=80',
  experience: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?w=600&q=80',
  donation: 'https://images.unsplash.com/photo-1448375240586-882707db888b?w=600&q=80',
};

function ImagePreviewModal({ src, title, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 px-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-2xl w-full rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <img src={src} alt={title} className="w-full object-cover max-h-[75vh]" />
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition text-lg leading-none"
        >
          &times;
        </button>
        {title && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-4 py-3">
            <p className="text-white text-sm font-medium">{title}</p>
          </div>
        )}
      </div>
    </div>
  );
}

const rewardTypeConfig = {
  discount: {
    icon: 'fas fa-tag',
    label: 'Discount',
    plural: 'Discounts',
    color: 'bg-blue-100 text-blue-700',
    gradient: 'from-blue-600 to-blue-700',
  },
  voucher: {
    icon: 'fas fa-ticket-alt',
    label: 'Voucher',
    plural: 'Vouchers',
    color: 'bg-purple-100 text-purple-700',
    gradient: 'from-purple-600 to-purple-700',
  },
  experience: {
    icon: 'fas fa-leaf',
    label: 'Eco Experience',
    plural: 'Eco Experiences',
    color: 'bg-green-100 text-green-700',
    gradient: 'from-[#004d3f] to-[#29752B]',
  },
  donation: {
    icon: 'fas fa-seedling',
    label: 'Donation',
    plural: 'Donations',
    color: 'bg-teal-100 text-teal-700',
    gradient: 'from-teal-600 to-teal-700',
  },
};

const REWARD_TYPES = ['discount', 'voucher', 'experience', 'donation'];

const FILTERS = [
  { key: 'all', label: 'All Rewards', icon: 'fas fa-th-large' },
  { key: 'discount', label: 'Discounts', icon: 'fas fa-tag' },
  { key: 'voucher', label: 'Vouchers', icon: 'fas fa-ticket-alt' },
  { key: 'experience', label: 'Eco Experiences', icon: 'fas fa-leaf' },
  { key: 'donation', label: 'Donations', icon: 'fas fa-seedling' },
];

const BLANK_FORM = { type: 'discount', description: '', pointsRequired: '', expiryDate: '' };

function EditModal({ reward, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: reward.type,
    description: reward.description,
    pointsRequired: reward.pointsRequired,
    expiryDate: reward.expiryDate ? reward.expiryDate.split('T')[0] : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await API.put(`/rewards/${reward._id}`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-[#004d3f] text-white">
          <h3 className="font-bold text-base flex items-center gap-2">
            <i className="fas fa-pen"></i> Edit Reward
          </h3>
          <button onClick={onClose} className="text-white/70 hover:text-white transition text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {REWARD_TYPES.map((t) => {
                const cfg = rewardTypeConfig[t];
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, type: t })}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 text-xs font-medium transition-all ${
                      form.type === t ? 'border-[#004d3f] bg-[#EEFAF7] text-[#004d3f]' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <i className={`${cfg.icon} text-base`}></i>
                    <span>{cfg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f] resize-none"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Points Required</label>
              <input
                type="number"
                min="1"
                value={form.pointsRequired}
                onChange={e => setForm({ ...form, pointsRequired: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f]"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Expiry Date <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="date"
                value={form.expiryDate}
                onChange={e => setForm({ ...form, expiryDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#004d3f]/30 focus:border-[#004d3f] text-gray-600"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-[#004d3f] text-white text-sm font-semibold hover:bg-[#003d32] disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving…</> : <><i className="fas fa-check"></i> Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RewardList({ userPoints = 0 }) {
  const [rewards, setRewards] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editTarget, setEditTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const isAdmin = localStorage.getItem('role') === 'admin';

  const fetchRewards = async () => {
    try {
      const response = await API.get('/rewards');
      setRewards(response.data);
    } catch {
      setError('Unable to load rewards. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRewards(); }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await API.delete(`/rewards/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setRewards(prev => prev.filter(r => r._id !== id));
    } catch {
      alert('Could not delete reward. Please try again.');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const filtered = filter === 'all' ? rewards : rewards.filter(r => r.type === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#004d3f] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading rewards…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <i className="fas fa-exclamation-triangle text-red-400 text-xl"></i>
        </div>
        <p className="text-red-600 font-medium text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {editTarget && (
        <EditModal
          reward={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchRewards(); }}
        />
      )}

      {previewImage && (
        <ImagePreviewModal
          src={previewImage.src}
          title={previewImage.title}
          onClose={() => setPreviewImage(null)}
        />
      )}

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-1">
        {FILTERS.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              filter === key
                ? 'bg-[#004d3f] text-white shadow-sm'
                : 'bg-white text-gray-600 border border-gray-200 hover:border-[#004d3f] hover:text-[#004d3f]'
            }`}
          >
            <i className={`${icon} text-xs`}></i>
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-gift text-gray-300 text-3xl"></i>
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-1">No rewards in this category</h3>
          <p className="text-sm text-gray-400">Check back soon — new rewards are added regularly.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((reward) => {
            const config = rewardTypeConfig[reward.type] || {
              icon: 'fas fa-gift', label: reward.type,
              color: 'bg-gray-100 text-gray-700',
              gradient: 'from-gray-500 to-gray-600',
            };
            const isExpired = reward.expiryDate && new Date(reward.expiryDate) < new Date();
            const daysLeft = reward.expiryDate
              ? Math.max(0, Math.ceil((new Date(reward.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)))
              : null;
            const canAfford = userPoints >= reward.pointsRequired;
            const isDeleting = deletingId === reward._id;
            const confirmingDelete = confirmDeleteId === reward._id;

            return (
              <div
                key={reward._id}
                className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col ${isExpired ? 'opacity-55' : ''}`}
              >
                {/* Clickable image preview */}
                <div
                  className="relative h-36 overflow-hidden cursor-pointer group bg-gray-100"
                  onClick={() => setPreviewImage({ src: typeImages[reward.type] || typeImages.discount, title: reward.description })}
                >
                  <img
                    src={typeImages[reward.type] || typeImages.discount}
                    alt={config.label}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full w-9 h-9 flex items-center justify-center shadow">
                      <i className="fas fa-expand-alt text-gray-700 text-sm"></i>
                    </div>
                  </div>
                  {isExpired && (
                    <div className="absolute inset-0 bg-gray-900/40 flex items-center justify-center">
                      <span className="text-white text-xs font-semibold bg-red-500/80 px-2 py-1 rounded-full">Expired</span>
                    </div>
                  )}
                </div>

                {/* Card top */}
                <div className={`bg-gradient-to-br ${config.gradient} p-5`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <i className={`${config.icon} text-white text-lg`}></i>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => setEditTarget(reward)}
                            title="Edit reward"
                            className="w-7 h-7 bg-white/20 hover:bg-white/35 rounded-lg flex items-center justify-center transition"
                          >
                            <i className="fas fa-pen text-white text-[11px]"></i>
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(reward._id)}
                            title="Delete reward"
                            className="w-7 h-7 bg-white/20 hover:bg-red-400/80 rounded-lg flex items-center justify-center transition"
                          >
                            <i className="fas fa-trash text-white text-[11px]"></i>
                          </button>
                        </>
                      )}
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/20 text-white capitalize">
                        {config.label}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1.5 text-white">
                    <i className="fas fa-coins text-yellow-300 text-sm"></i>
                    <span className="text-2xl font-bold">{reward.pointsRequired}</span>
                    <span className="text-white/70 text-sm">pts</span>
                  </div>
                  {localStorage.getItem('token') && !isExpired && (
                    <div className={`mt-2 text-xs ${canAfford ? 'text-green-200' : 'text-white/50'}`}>
                      {canAfford ? '✓ You can afford this' : `You need ${reward.pointsRequired - userPoints} more pts`}
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className="p-4 flex flex-col flex-1">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{reward.description}</p>

                  {/* Expiry badge */}
                  {reward.expiryDate && (
                    <div className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg mb-3 self-start ${
                      isExpired ? 'bg-red-100 text-red-600' : daysLeft <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <i className={`fas fa-${isExpired ? 'times-circle' : 'clock'} text-[10px]`}></i>
                      {isExpired ? 'Expired' : daysLeft === 0 ? 'Expires today' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                    </div>
                  )}

                  {/* Delete confirmation inline */}
                  {confirmingDelete && isAdmin ? (
                    <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                      <p className="text-xs text-red-700 font-medium mb-2">Delete this reward permanently?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="flex-1 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDelete(reward._id)}
                          disabled={isDeleting}
                          className="flex-1 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 disabled:opacity-60 transition flex items-center justify-center gap-1"
                        >
                          {isDeleting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>}
                          {isDeleting ? 'Deleting…' : 'Yes, delete'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {!isExpired && localStorage.getItem('token') && !isAdmin && (
                        <RedeemButton rewardId={reward._id} pointsRequired={reward.pointsRequired} canAfford={canAfford} />
                      )}
                      {isAdmin && !confirmingDelete && (
                        <div className="text-xs text-center text-gray-400 py-2 bg-gray-50 rounded-xl">
                          Admin view — use the pencil icon to edit
                        </div>
                      )}
                      {!localStorage.getItem('token') && (
                        <a href="/login" className="block w-full text-center py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-[#004d3f] hover:text-white transition-all">
                          <i className="fas fa-lock mr-1.5"></i>Log in to redeem
                        </a>
                      )}
                      {isExpired && !isAdmin && (
                        <div className="w-full text-center py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400">
                          <i className="fas fa-ban mr-1.5"></i>No longer available
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="mt-10 p-4 bg-[#EEFAF7] rounded-2xl border border-[#C1E8CD] text-center">
        <p className="text-[#1B5E20] text-sm font-medium mb-1">🌿 e-save does not offer cash payments</p>
        <p className="text-[#414042] text-xs">All rewards are eco vouchers, partner discounts, or green experiences. Keep recycling to earn more points!</p>
      </div>
    </div>
  );
}

export default RewardList;
